import { extendTheme } from "@chakra-ui/react";
import { ReiButton as Button } from "./components/button";
import { ReiContainer as Container } from "./components/container";
import { ReiFormLabel as FormLabel } from "./components/form-label";
import { ReiHeading as Heading } from "./components/heading";
import { ReiInput as Input } from "./components/input";
import { ReiModal as Modal } from "./components/modal";
import { ReiSelect as Select } from "./components/select";
import {
  Alert,
  Card,
  Drawer,
  Menu,
  NumberInput,
  Table,
  Tabs,
  Textarea,
} from "./components/misc";

// For theme reference: https://chakra-ui.com/docs/styled-system/theming/customize-theme
// Tokens mirror the landing page design system (tailwind.config.js)

// Indigo brand scale: 500 = primary-container (#4f46e5), 600 = primary (#3525cd)
const brand = {
  50: "#f1f3ff",
  100: "#e2dfff",
  200: "#c3c0ff",
  300: "#a29bfb",
  400: "#7d73f0",
  500: "#4f46e5",
  600: "#3525cd",
  700: "#2a1db0",
  800: "#1f148a",
  900: "#0f0069",
};

const theme = extendTheme({
  fonts: {
    heading: `"Plus Jakarta Sans", system-ui, sans-serif`,
    body: `Inter, system-ui, sans-serif`,
  },

  styles: {
    global: {
      body: {
        bg: "surface.DEFAULT",
        color: "ink.DEFAULT",
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "1.6",
        WebkitFontSmoothing: "antialiased",
      },
    },
  },

  colors: {
    brand,
    // Existing pages use colorScheme="teal" / "blue" for primary actions,
    // so both map to the brand indigo
    teal: brand,
    blue: brand,
    surface: {
      DEFAULT: "#f9f9ff",
      lowest: "#ffffff",
      low: "#f1f3ff",
      container: "#e9edff",
      high: "#e1e8fd",
      highest: "#dce2f7",
    },
    ink: {
      DEFAULT: "#141b2b",
      muted: "#464555",
      subtle: "#777587",
    },
    line: {
      DEFAULT: "#c7c4d8",
      soft: "rgba(99, 102, 241, 0.12)",
    },
    success: {
      50: "#e6fbf8",
      100: "#c4f5ee",
      500: "#006d62",
      600: "#00534a",
    },
    warm: { 500: "#E26839" },
    rei: {
      black: "#141b2b",
      dark_navy: "#092152",
      navy: "#334267",
      light_navy: "#68738B",
      turquoise: "#4f46e5",
      orange: "#E26839",
      yellow: "#FFC84E",
      white: "#FFFFFF",
      light_grey: "#D1D6E0",
      success: "#00534a",
      red: "#ba1a1a",
      linear: "#f1f3ff",
    },
  },

  radii: {
    sm: "0.25rem",
    base: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    "2xl": "1.25rem",
    "3xl": "1.5rem",
  },

  shadows: {
    sm: "0 1px 3px rgba(17, 24, 39, 0.05)",
    base: "0 2px 8px -2px rgba(79, 70, 229, 0.08), 0 1px 3px rgba(17, 24, 39, 0.04)",
    md: "0 4px 20px -2px rgba(79, 70, 229, 0.06), 0 2px 6px -1px rgba(17, 24, 39, 0.04)",
    lg: "0 20px 25px -5px rgba(37, 99, 235, 0.08), 0 8px 10px -6px rgba(17, 24, 39, 0.03)",
    xl: "0 25px 50px -12px rgba(17, 24, 39, 0.15)",
    "2xl": "0 25px 50px -12px rgba(17, 24, 39, 0.2)",
    outline: "0 0 0 3px rgba(79, 70, 229, 0.25)",
  },

  components: {
    Alert,
    Button,
    Card,
    Container,
    Drawer,
    FormLabel,
    Heading,
    Input,
    Menu,
    Modal,
    NumberInput,
    Select,
    Table,
    Tabs,
    Textarea,
  },
});

export default theme;
